/**
 * @module rackUtils
 * @description Rack tile drag-and-drop, click, and position-recalculation
 * logic for the Game component.
 */

/**
 * @typedef {object} MouseDownDeps
 * @property {function(object): void} setGrabbedTile - Sets the tile currently being dragged.
 * @property {function(number): void} setLastClientX - Sets the last known drag X position.
 * @property {function(number): void} setMouseDownClientX - Sets the X position at mouse-down.
 */

/**
 * Initiates a rack tile drag operation by recording the grabbed tile and
 * the starting pointer position.
 * @param {MouseEvent} event
 * @param {object} tile - The rack tile being grabbed.
 * @param {MouseDownDeps} deps
 * @returns {void}
 */
export function mouseDown(event, tile, deps) {
    const { setGrabbedTile, setLastClientX, setMouseDownClientX } = deps;
    setGrabbedTile(tile);
    setLastClientX(event.clientX);
    setMouseDownClientX(event.clientX);
}

/**
 * @typedef {object} MouseUpDeps
 * @property {function(object|null): void} setGrabbedTile - Clears the grabbed tile.
 * @property {function(boolean): void} setTileMoved - Resets the tile-moved flag.
 */

/**
 * Ends a rack tile drag operation.
 * @param {MouseEvent} event
 * @param {MouseUpDeps} deps
 * @returns {void}
 */
export function mouseUp(event, deps) {
    const { setGrabbedTile, setTileMoved } = deps;
    setGrabbedTile(null);
    setTileMoved(false);
}

/**
 * @typedef {object} MouseClickOnRackDeps
 * @property {number} mouseDownClientX - The X position recorded at mouse-down.
 * @property {Array} rackTiles - Current rack tile array.
 * @property {function(Array): void} setRackTiles - Rack tiles setter.
 * @property {function(object): void} setPickedUpTile - Records the tile that was picked up.
 * @property {function(object): void} setFloatingTile - Promotes the tile to a floating tile.
 * @property {function(object|null): void} setGrabbedTile - Clears the grabbed tile.
 */

/**
 * Handles a click on a rack tile: if the pointer hasn't moved significantly
 * since mouse-down, removes the tile from the rack and promotes it to a
 * floating tile.
 * @param {MouseEvent} event
 * @param {object} clickedTile - The rack tile that was clicked.
 * @param {MouseClickOnRackDeps} deps
 * @returns {void}
 */
export function mouseClickOnRack(event, clickedTile, deps) {
    const { mouseDownClientX, rackTiles, setRackTiles, setPickedUpTile, setFloatingTile, setGrabbedTile } = deps;
    if (Math.abs(mouseDownClientX - event.clientX) < 2) {
        setRackTiles([...rackTiles.filter((tile) => tile !== clickedTile)]);
        //TODO: What was this used for?
        setPickedUpTile(clickedTile);
        setFloatingTile(clickedTile);
        setGrabbedTile(null);
    }
}

/**
 * @typedef {object} MovingDeps
 * @property {object|null} grabbedTile - The tile currently being dragged.
 * @property {Array} rackTiles - Current rack tile array.
 * @property {number} lastClientX - The X position from the previous mousemove event.
 * @property {number} rackScale - Current rack tile scale factor (pixels per tile unit).
 * @property {function(boolean): void} setTileMoved - Marks that the tile has moved.
 * @property {function(object|null): void} setGrabbedTile - Updates or clears the grabbed tile.
 * @property {function(number): void} setLastClientX - Updates the last known drag X position.
 * @property {function(Array): void} setRackTiles - Rack tiles setter.
 */

/**
 * Handles mousemove during a rack drag: updates the grabbed tile's position and
 * pushes neighbouring tiles out of the way to prevent overlap.
 * @param {MouseEvent} event
 * @param {MovingDeps} deps
 * @returns {void}
 */
export function moving(event, deps) {
    const { grabbedTile, rackTiles, lastClientX, rackScale, setTileMoved, setGrabbedTile, setLastClientX, setRackTiles } = deps;
    if (grabbedTile) {
        setTileMoved(true);

        let index = rackTiles.indexOf(grabbedTile);

        let movementX = (event.clientX - lastClientX) * (75 / rackScale);
        let newX = grabbedTile.position + movementX;

        if (newX < index * 75) {
            newX = index * 75;
            setGrabbedTile(null);
        }

        // if (newX > 699 - 75) {
        //     newX = 625;
        // }

        let checkLeftIndex = index;
        while (checkLeftIndex > 0 && movementX < 0) {
            if (
                rackTiles[checkLeftIndex - 1].position >
                newX - (index - checkLeftIndex + 1) * 75
            ) {
                rackTiles[checkLeftIndex - 1].position =
                    newX - (index - checkLeftIndex + 1) * 75;
            }
            checkLeftIndex--;
        }
        // console.log("RIGHTMOST POSITION", newX, rackTiles.length, index)
        if (newX > 697 - 75 - (rackTiles.length - 1 - index) * 75) {
            newX = 697 - 75 - (rackTiles.length - 1 - index) * 75;
            setGrabbedTile(null);
        }

        let checkRightIndex = index;
        while (checkRightIndex < rackTiles.length - 1) {
            let nextIndex = checkRightIndex + 1;
            // console.log(
            //     "RIGHT INDEX",
            //     checkRightIndex,
            //     nextIndex,
            //     rack[nextIndex]
            // );

            if (movementX > 0) {
                if (
                    rackTiles[nextIndex].position <
                    newX + (checkRightIndex - index + 1) * 75
                ) {
                    rackTiles[nextIndex].position =
                        newX + (checkRightIndex - index + 1) * 75;
                }
            }
            checkRightIndex++;
        }

        grabbedTile.position = newX;

        setLastClientX(event.clientX);
        setRackTiles([...rackTiles]);
    }
}

/**
 * @typedef {object} AddTileToRackDeps
 * @property {{ left: number, right: number }} rackBoundingBox - Bounding rect of the rack element.
 * @property {Array} rackTiles - Current rack tile array.
 * @property {object} game - Current game state.
 * @property {function(Array): void} setRackTiles - Rack tiles setter.
 * @property {function(object): void} setGame - Game state setter.
 */

/**
 * Inserts a tile into the rack at the drop x-coordinate, recalculating positions
 * of surrounding tiles to prevent overlap.
 * @param {object} tile - The tile to insert.
 * @param {number} x - The clientX of the drop event (0 means use rack left edge).
 * @param {AddTileToRackDeps} deps
 * @returns {void}
 */
export function addTileToRack(tile, x, deps) {
    const { rackBoundingBox, rackTiles, game, setRackTiles, setGame } = deps;
    console.log("ADDING TO RACK", tile, x);

    if (x === 0) {
        x = rackBoundingBox.left;
    }

    // determine rackScale position of cursor
    const rackRatio = (rackBoundingBox.right - rackBoundingBox.left) / 700;
    // find where in rack tile is being dropped
    // const tileDropPosition = x - rackBoundingBox.left - 75 * rackRatio;
    const tileDropPosition = x - 75 * rackRatio;
    tile.position = tileDropPosition + 10 * rackRatio;

    console.log("ADDING TO RACK", x, rackBoundingBox.left, rackRatio, tileDropPosition);

    // move tiles to the left
    const droppedAfterIndex = rackTiles.reduce(
        (droppedAfter, tile, index) => {
            if (tile.position <= tileDropPosition) {
                return index;
            } else {
                return droppedAfter;
            }
        },
        -1
    );

    console.log(tileDropPosition, droppedAfterIndex);
    console.log(
        rackTiles.slice(0, droppedAfterIndex + 1),
        rackTiles.slice(droppedAfterIndex + 1)
    );
    console.log("ADDING TILE BACK TO RACK");
    const newRackTiles = [
        ...rackTiles.slice(0, droppedAfterIndex + 1),
        tile,
        ...rackTiles.slice(droppedAfterIndex + 1),
    ];

    // move tiles to the right
    for (let i = droppedAfterIndex; i >= 0; i--) {
        if (
            newRackTiles[i].position >
            newRackTiles[i + 1].position - 75
        ) {
            newRackTiles[i].position =
                newRackTiles[i + 1].position - 75;
        } else {
            break;
        }
    }
    if (newRackTiles[0].position < 0) {
        newRackTiles[0].position = 0;
        for (let i = 1; i < newRackTiles.length; i++) {
            if (
                newRackTiles[i].position <
                newRackTiles[i - 1].position + 75
            ) {
                newRackTiles[i].position =
                    newRackTiles[i - 1].position + 75;
            } else {
                break;
            }
        }
    }
    for (
        let i = droppedAfterIndex + 1;
        i < newRackTiles.length - 1;
        i++
    ) {
        if (
            newRackTiles[i + 1].position <
            newRackTiles[i].position + 75
        ) {
            newRackTiles[i + 1].position =
                newRackTiles[i].position + 75;
        } else {
            break;
        }
    }

    if (newRackTiles[newRackTiles.length - 1].position > 700 - 75) {
        newRackTiles[newRackTiles.length - 1].position = 700 - 75;
        for (let i = newRackTiles.length - 2; i >= 0; i--) {
            if (
                newRackTiles[i].position >
                newRackTiles[i + 1].position - 75
            ) {
                newRackTiles[i].position =
                    newRackTiles[i + 1].position - 75;
            } else {
                break;
            }
        }
    }

    // update rackTiles with tile in new position
    setRackTiles(newRackTiles);
    setGame({ ...game, rack: newRackTiles });
}
